import { getSearchSlug, HOME_SLUG, TRADER_PROFILE_SLUG } from "src/constants/urls";

export const createWikifolioDetailBreadcrumbSchema = (canonicalUrl: string, dict: { home: string; allWikifolios: string }, wikifolioName: string) => {
    const { origin } = new URL(canonicalUrl);

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: dict.home,
                item: `${origin}/de/de/${HOME_SLUG}`,
            },
            {
                "@type": "ListItem",
                position: 2,
                name: dict.allWikifolios,
                item: `${origin}/de/de/${getSearchSlug("de")}`,
            },
            {
                "@type": "ListItem",
                position: 3,
                name: wikifolioName,
                item: canonicalUrl,
            },
        ],
    };
};

export const createHomeSearchActionSchema = (language: string, country: string) => {
    const websiteUrl = `https://www.wikifolio.com/${language}/${country}`;
    const homeUrl = `${websiteUrl}/${HOME_SLUG}`;

    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${homeUrl}#website`,
        url: homeUrl,
        name: "wikifolio.com",
        inLanguage: language,
        potentialAction: {
            "@type": "SearchAction",
            target: `${websiteUrl}/${getSearchSlug(language)}?searchTerm={search_term_string}`,
            "query-input": "required name=search_term_string",
        },
    };
};

export const ORGANIZATION_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Organization",
    url: "https://www.wikifolio.com",
    logo: "https://wikifolio.imgix.net/prod/images/wikifolio-logo.png",
    foundingDate: "2012",
    founder: {
        "@type": "Person",
        name: "Andreas Kern",
    },
    address: {
        "@type": "PostalAddress",
        streetAddress: "Berggasse 31",
        addressLocality: "Vienna",
        postalCode: "1090",
        addressCountry: "Austria",
    },
};

const ORGANIZATION_SCHEMA_CONTACT_POINTS = [
    {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: "service@wikifolio.com",
        telephone: "+49-211-24790770",
        areaServed: "DE",
        availableLanguage: ["de", "en"],
    },
    {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: "service@wikifolio.com",
        telephone: "+43-720-30381270",
        areaServed: ["AT", "CH"],
        availableLanguage: ["de", "en"],
    },
];

const ORGANIZATION_SCHEMA_SAME_AS = [
    "https://www.facebook.com/wikifolio",
    "https://www.instagram.com/wikifolio/",
    "https://x.com/wikifolio",
    "https://at.linkedin.com/company/wikifolio-financial-technologies-gmbh",
    "https://www.youtube.com/user/wikifoliocom",
];

const ORGANIZATION_SCHEMA_BASE = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.wikifolio.com/en/int/home#organization",
    name: "wikifolio Financial Technologies AG",
    url: "https://www.wikifolio.com/",
    logo: "https://wikifolio.imgix.net/prod-cms-media/636098/wikifolio-logo.png",
    foundingDate: "2011",
    contactPoint: ORGANIZATION_SCHEMA_CONTACT_POINTS,
    sameAs: ORGANIZATION_SCHEMA_SAME_AS,
};

export const EN_ORGANIZATION_SCHEMA = {
    ...ORGANIZATION_SCHEMA_BASE,
    description:
        "wikifolio is Europe’s leading platform for transparent and active investment strategies, enabling investors to access exchange-traded financial products based on the strategies of experienced traders and investors.",
};

export const DE_ORGANIZATION_SCHEMA = {
    ...ORGANIZATION_SCHEMA_BASE,
    description:
        "wikifolio ist Europas führende Plattform für transparente und aktive Investment-Strategien, die Anlegern den Zugang zu börsengehandelten Finanzprodukten ermöglicht, die auf den Strategien erfahrener Trader und Anleger basieren.",
};

const PRODUCT_DESCRIPTION_MAX_LENGTH = 300;

const truncateAtWordBoundary = (text: string, maxLength: number) => {
    const normalized = text.replace(/\s+/g, " ").trim();

    if (normalized.length <= maxLength) {
        return normalized;
    }

    const sliced = normalized.slice(0, maxLength);
    const lastSpaceIndex = sliced.lastIndexOf(" ");

    return `${lastSpaceIndex === -1 ? sliced : sliced.slice(0, lastSpaceIndex)}…`;
};

interface IPropertyValue {
    "@type": "PropertyValue";
    name: string;
    value: string;
    unitText?: string;
}

const toPercentPropertyValue = (name: string, value: number | null): IPropertyValue | null =>
    value === null
        ? null
        : {
              "@type": "PropertyValue",
              name,
              value: (value * 100).toFixed(1),
              unitText: "%",
          };

export interface IWikifolioProductSchemaParams {
    canonicalUrl: string;
    name: string;
    descriptionContent: string;
    keywords: string[];
    sku: string;
    isin: string | null;
    traderNickName: string;
    tradingCurrency: string;
    investedCapital: { value: number; currency: string } | null;
    creationDate: string;
    performanceSinceCreation: number | null;
    averageAnnualPerformance: number | null;
    performanceOneYear: number | null;
    volatilityOneYear: number | null;
    image: string;
    price: number | null;
    priceCurrency: string | null;
}

export const createWikifolioDetailProductSchema = (params: IWikifolioProductSchemaParams) => {
    const {
        canonicalUrl,
        name,
        descriptionContent,
        keywords,
        sku,
        isin,
        traderNickName,
        tradingCurrency,
        investedCapital,
        creationDate,
        performanceSinceCreation,
        averageAnnualPerformance,
        performanceOneYear,
        volatilityOneYear,
        image,
        price,
        priceCurrency,
    } = params;

    const { origin } = new URL(canonicalUrl);
    const description = truncateAtWordBoundary(descriptionContent, PRODUCT_DESCRIPTION_MAX_LENGTH);
    const additionalProperty = [
        ...(isin ? [{ "@type": "PropertyValue", name: "ISIN", value: isin } as IPropertyValue] : []),
        { "@type": "PropertyValue", name: "Trading currency", value: tradingCurrency } as IPropertyValue,
        toPercentPropertyValue("Performance since creation", performanceSinceCreation),
        toPercentPropertyValue("Average annual performance", averageAnnualPerformance),
        toPercentPropertyValue("Performance 1 year", performanceOneYear),
        toPercentPropertyValue("Volatility 1 year", volatilityOneYear),
        ...(investedCapital
            ? [
                  {
                      "@type": "PropertyValue",
                      name: "Invested capital",
                      value: String(Math.round(investedCapital.value)),
                      unitText: investedCapital.currency,
                  } as IPropertyValue,
              ]
            : []),
        { "@type": "PropertyValue", name: "Creation date", value: creationDate } as IPropertyValue,
    ].filter((property): property is IPropertyValue => property !== null);

    return {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${canonicalUrl}#product`,
        name,
        url: canonicalUrl,
        image,
        ...(description ? { description } : {}),
        ...(keywords.length ? { keywords } : {}),
        sku,
        ...(isin ? { mpn: isin } : {}),
        brand: { "@id": ORGANIZATION_SCHEMA_BASE["@id"] },
        creator: { "@id": `${origin}/de/de/${TRADER_PROFILE_SLUG}/${traderNickName.toLowerCase()}#person` },
        offers: {
            "@type": "Offer",
            url: canonicalUrl,
            availability: "https://schema.org/InStock",
            ...(price !== null && priceCurrency !== null ? { price: price.toFixed(2), priceCurrency } : {}),
        },
        additionalProperty,
    };
};
